import { createRouter, createWebHistory } from 'vue-router'
import { fetchBoot, session } from './session'
import { canAccessRoute } from './rbac'


const routes = [
  { path: '/login', name: 'Login', component: () => import('@/pages/Login.vue'), meta: { public: true, layout: 'auth' },
  },
  { path: '/signup', name: 'Signup', component: () => import('@/pages/Signup.vue'), meta: { public: true, layout: 'auth' },
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: () => import('@/pages/ForgotPassword.vue'),
    meta: { public: true, layout: 'auth' },
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: () => import('@/pages/ResetPassword.vue'),
    meta: { public: true, layout: 'auth' },
  },
  { path: '/', name: 'Dashboard', component: () => import('@/pages/Dashboard.vue'), meta: { permission: 'portal.access' } },
  {
    path: '/members',
    name: 'Members',
    component: () => import('@/pages/Members.vue'),
    meta: { permission: 'members.view', module: 'membership' },
  },
  {
    path: '/members/:name',
    name: 'MemberDetail',
    component: () => import('@/pages/MemberDetail.vue'),
    meta: { permission: 'members.view', module: 'membership' },
  },
  {
    path: '/households',
    name: 'Households',
    component: () => import('@/pages/Households.vue'),
    meta: { permission: 'households.view', module: 'membership' },
  },
  {
    path: '/households/:name',
    name: 'HouseholdDetail',
    component: () => import('@/pages/HouseholdDetail.vue'),
    meta: { permission: 'households.view', module: 'membership' },
  },
  {
    path: '/giving',
    name: 'Giving',
    component: () => import('@/pages/Giving.vue'),
    meta: { permission: 'giving.view', module: 'giving' },
  },
  {
    path: '/pledges',
    name: 'Pledges',
    component: () => import('@/pages/Pledges.vue'),
    meta: { permission: 'pledges.view', module: 'giving' },
  },

  {
    path: '/funds',
    name: 'Funds',
    component: () => import('@/pages/Funds.vue'),
    meta: { permission: 'funds.view', module: 'giving' },
  },
  {
    path: '/funds/:name',
    name: 'FundDetail',
    component: () => import('@/pages/FundDetail.vue'),
    meta: { permission: 'funds.view', module: 'giving' },
  },
  {
    path: '/campaigns',
    name: 'Campaigns',
    component: () => import('@/pages/Campaigns.vue'),
    meta: { permission: 'funds.view', module: 'giving' },
  },
  {
    path: '/campaigns/:name',
    name: 'CampaignDetail',
    component: () => import('@/pages/CampaignDetail.vue'),
    meta: { permission: 'funds.view', module: 'giving' },
  },
  {
    path: '/expenses',
    name: 'Expenses',
    component: () => import('@/pages/Expenses.vue'),
    meta: { permission: 'expenses.view', module: 'giving' },
  },
  {
    path: '/expenses/summary',
    name: 'ExpenseSummary',
    component: () => import('@/pages/ExpenseSummary.vue'),
    meta: { permission: 'expenses.view', module: 'giving' },
  },
  {
    path: '/expenses/:name',
    name: 'ExpenseDetail',
    component: () => import('@/pages/ExpenseDetail.vue'),
    meta: { permission: 'expenses.view', module: 'giving' },
  },
  {
    path: '/disbursements',
    name: 'Disbursements',
    component: () => import('@/pages/Expenses.vue'),
    meta: {
      permission: 'disbursements.view',
      module: 'giving',
      defaultSource: 'disbursement' },
  },
  {
    path: '/fund-transfers',
    name: 'FundTransfers',
    component: () => import('@/pages/FundTransfers.vue'),
    meta: { permission: 'fund_transfers.view', module: 'giving' },
  },
  {
    path: '/fund-transfers/:name',
    name: 'FundTransferDetail',
    component: () => import('@/pages/FundTransferDetail.vue'),
    meta: { permission: 'fund_transfers.view', module: 'giving' },
  },
  {
    path: '/welfare',
    name: 'WelfareCases',
    component: () => import('@/pages/WelfareCases.vue'),
    meta: { permission: 'welfare.view', module: 'giving' },
  },
  {
    path: '/sacraments',
    name: 'Sacraments',
    component: () => import('@/pages/Sacraments.vue'),
    meta: { permission: 'sacraments.view', module: 'sacraments' },
  },
  {
    path: '/events',
    name: 'Events',
    component: () => import('@/pages/Events.vue'),
    meta: { permission: 'events.view' },
  },
  {
    path: '/events/:name',
    name: 'EventDetail',
    component: () => import('@/pages/EventDetail.vue'),
    meta: { permission: 'events.view' },
  },
  {
    path: '/mass-intentions',
    name: 'MassIntentions',
    component: () => import('@/pages/MassIntentions.vue'),
    meta: { permission: 'mass_intentions.view', module: 'liturgy' },
  },
  {
    path: '/mass-intentions/:name',
    name: 'MassIntentionDetail',
    component: () => import('@/pages/MassIntentionDetail.vue'),
    meta: { permission: 'mass_intentions.view', module: 'liturgy' },
  },
  {
    path: '/mass-calendar',
    name: 'MassCalendar',
    component: () => import('@/pages/MassCalendar.vue'),
    meta: { permission: 'mass_intentions.view', module: 'liturgy' },
  },
  {
    path: '/mass-request',
    name: 'MassRequest',
    component: () => import('@/pages/MassRequest.vue'),
    meta: { public: true },
  },
  {
    path: '/mass-schedules',
    name: 'MassSchedules',
    component: () => import('@/pages/MassSchedules.vue'),
    meta: { permission: 'mass_intentions.manage', module: 'liturgy' },
  },
  {
    path: '/gregorian-series',
    name: 'GregorianSeries',
    component: () => import('@/pages/GregorianSeries.vue'),
    meta: { permission: 'mass_intentions.manage', module: 'liturgy' },
  },
  {
    path: '/sacristy',
    name: 'SacristyBoard',
    component: () => import('@/pages/SacristyBoard.vue'),
    meta: { permission: 'mass_intentions.sacristy', module: 'liturgy' },
  },
  {
    path: '/today',
    name: 'Today',
    component: () => import('@/pages/Today.vue'),
    meta: { permission: 'mass_intentions.sacristy', module: 'liturgy' },
  },
  {
    path: '/mass-schedule-public',
    name: 'MassPublicSchedule',
    component: () => import('@/pages/MassPublicSchedule.vue'),
    meta: { public: true },
  },
  {
    path: '/verify-sacrament',
    name: 'VerifySacrament',
    component: () => import('@/pages/VerifySacrament.vue'),
    meta: { public: true },
  },
  {
    path: '/ministry',
    name: 'Ministry',
    component: () => import('@/pages/Ministry.vue'),
    meta: { permission: 'ministry.view', module: 'ministry' },
  },
  {
    path: '/compliance',
    name: 'Compliance',
    component: () => import('@/pages/Compliance.vue'),
    meta: {
      permission: 'compliance.view',
      module: 'safe_environment' },
  },
  {
    path: '/formation',
    name: 'Formation',
    component: () => import('@/pages/Formation.vue'),
    meta: {
      permission: 'formation.view',
      module: 'formation' },
  },
  {
    path: '/prayer-requests',
    name: 'PrayerRequests',
    component: () => import('@/pages/PrayerRequests.vue'),
    meta: { permission: 'prayer.view', module: 'portal' },
  },
  {
    path: '/facility-bookings',
    name: 'FacilityBookings',
    component: () => import('@/pages/FacilityBookings.vue'),
    meta: { permission: 'facilities.view', module: 'events' },
  },
  {
    path: '/reporting',
    name: 'DiocesanReporting',
    component: () => import('@/pages/DiocesanReporting.vue'),
    meta: {
      permission: 'reporting.diocesan' },
  },
  {
    path: '/facilities/assets',
    name: 'Assets',
    component: () => import('@/pages/Assets.vue'),
    meta: { permission: 'assets.view', module: 'assets' },
  },
  {
    path: '/facilities/assets/:name',
    name: 'AssetDetail',
    component: () => import('@/pages/AssetDetail.vue'),
    meta: { permission: 'assets.view', module: 'assets' },
  },
  {
    path: '/inventory',
    name: 'Inventory',
    component: () => import('@/pages/Inventory.vue'),
    meta: { permission: 'inventory.view', module: 'inventory' },
  },
  {
    path: '/equipment/:name',
    name: 'EquipmentRequestDetail',
    component: () => import('@/pages/EquipmentRequestDetail.vue'),
    meta: { permission: 'inventory.view', module: 'inventory' },
  },
  {
    path: '/staff',
    name: 'Staff',
    component: () => import('@/pages/Staff.vue'),
    meta: { permission: 'hr.view', module: 'hr' },
  },
  {
    path: '/staff/:name',
    name: 'StaffDetail',
    component: () => import('@/pages/StaffDetail.vue'),
    meta: { permission: 'hr.view', module: 'hr' },
  },
  {
    path: '/settings',
    name: 'ParishSettings',
    component: () => import('@/pages/ParishSettings.vue'),
    meta: {
      permission: 'settings.view',
      module: 'settings' },
  },
  {
    path: '/settings/permissions',
    name: 'Permissions',
    component: () => import('@/pages/Permissions.vue'),
    // Read-only without permissions.manage; every write is gated again server-side.
    meta: { permission: 'settings.view', module: 'settings' },
  },
  {
    path: '/support',
    name: 'Support',
    component: () => import('@/pages/Support.vue'),
    meta: {
      permission: 'support.view' },
  },
  {
    path: '/support/:name',
    name: 'SupportTicketDetail',
    component: () => import('@/pages/SupportTicketDetail.vue'),
    meta: {
      permission: 'support.view' },
  },
  {
    path: '/notifications',
    name: 'Notifications',
    component: () => import('@/pages/Notifications.vue'),
    meta: { permission: 'portal.access' },
  },
  {
    path: '/whats-new',
    name: 'WhatsNew',
    component: () => import('@/pages/WhatsNew.vue'),
    meta: { permission: 'portal.access' },
  },
  {
    path: '/site-pages',
    name: 'SitePages',
    component: () => import('@/pages/SitePages.vue'),
    meta: { permission: 'settings.view', module: 'settings' },
  },
  {
    path: '/templates',
    name: 'Templates',
    component: () => import('@/pages/Templates.vue'),
    meta: { permission: 'messaging.view', module: 'settings' },
  },
  {
    path: '/fund-types',
    name: 'FundTypes',
    component: () => import('@/pages/FundTypes.vue'),
    meta: { permission: 'funds.view', module: 'giving' },
  },
  {
    path: '/public/:slug?',
    name: 'PublicSite',
    component: () => import('@/pages/PublicSite.vue'),
    meta: { public: true },
  },
  {
    path: '/formation/:name',
    name: 'FormationProgramDetail',
    component: () => import('@/pages/FormationProgramDetail.vue'),
    meta: { permission: 'formation.view', module: 'formation' },
  },
  {
    path: '/sacraments/record/:name',
    name: 'SacramentRecordDetail',
    component: () => import('@/pages/SacramentRecordDetail.vue'),
    meta: { permission: 'sacraments.view', module: 'sacraments' },
  },
  {
    path: '/messaging/:name',
    name: 'MessagingCampaignDetail',
    component: () => import('@/pages/MessagingCampaignDetail.vue'),
    meta: { permission: 'messaging.view', module: 'communications' },
  },
  {
    path: '/giving/unassigned',
    name: 'UnassignedGifts',
    component: () => import('@/pages/UnassignedGifts.vue'),
    meta: { permission: 'giving.view', module: 'giving' },
  },
  {
    path: '/giving/statement',
    name: 'GivingStatement',
    component: () => import('@/pages/GivingStatement.vue'),
    meta: { permission: 'giving.view', module: 'giving' },
  },
  {
    path: '/giving/:name',
    name: 'ContributionDetail',
    component: () => import('@/pages/ContributionDetail.vue'),
    meta: { permission: 'giving.view', module: 'giving' },
  },
  {
    path: '/gregorian-series/:name',
    name: 'GregorianSeriesDetail',
    component: () => import('@/pages/GregorianSeriesDetail.vue'),
    meta: { permission: 'mass_intentions.manage', module: 'liturgy' },
  },
  {
    path: '/mass-schedules/:name',
    name: 'MassScheduleDetail',
    component: () => import('@/pages/MassScheduleDetail.vue'),
    meta: { permission: 'mass_intentions.manage', module: 'liturgy' },
  },
  {
    path: '/facility-bookings/:name',
    name: 'FacilityBookingDetail',
    component: () => import('@/pages/FacilityBookingDetail.vue'),
    meta: { permission: 'facilities.view', module: 'events' },
  },
  {
    path: '/pledges/:name',
    name: 'PledgeDetail',
    component: () => import('@/pages/PledgeDetail.vue'),
    meta: { permission: 'pledges.view', module: 'giving' },
  },
  {
    path: '/admin/tenants',
    name: 'TenantAdmin',
    component: () => import('@/pages/admin/TenantAdmin.vue'),
    meta: { permission: 'platform.manage' },
  },

  {
    path: '/messaging',
    name: 'Messaging',
    component: () => import('@/pages/Messaging.vue'),
    meta: { permission: 'messaging.view', module: 'communications' },
  },
  {
    path: '/media',
    name: 'Media',
    component: () => import('@/pages/Media.vue'),
    meta: { permission: 'media.view' },
  },
  {
    path: '/admin/users',
    name: 'AdminUsers',
    component: () => import('@/pages/admin/Users.vue'),
    meta: { permission: 'admin.users.view' },
  },
  {
    path: '/admin/profile-requests',
    name: 'AdminProfileRequests',
    component: () => import('@/pages/admin/ProfileRequests.vue'),
    meta: {
      permission: 'members.approve_profile',
      module: 'membership' },
  },
  {
    path: '/plan',
    name: 'Plan',
    component: () => import('@/pages/Plan.vue'),
    meta: { permission: 'plan.view' },
  },
  { path: '/my', name: 'MyHub', component: () => import('@/pages/my/Hub.vue'), meta: { permission: 'portal.access' },
  },
  {
    path: '/my/giving',
    name: 'MyGiving',
    component: () => import('@/pages/my/Giving.vue'),
    meta: { permission: 'portal.access' },
  },
  {
    path: '/my/pledges',
    name: 'MyPledges',
    component: () => import('@/pages/my/Pledges.vue'),
    meta: { permission: 'portal.access' },
  },
  {
    path: '/my/pledges/:name',
    name: 'MyPledgeDetail',
    component: () => import('@/pages/my/PledgeDetail.vue'),
    // own pledges only: the API answers 'not found' for anyone else's
    meta: { permission: 'portal.access' },
  },
  {
    path: '/my/household',
    name: 'MyHousehold',
    component: () => import('@/pages/my/Household.vue'),
    // a portal member manages their own household: portal access, not the staff capability
    meta: { permission: 'portal.access' },
  },
  {
    path: '/my/profile',
    name: 'MyProfile',
    component: () => import('@/pages/my/Profile.vue'),
    meta: { permission: 'portal.access' },
  },
  {
    path: '/my/events',
    name: 'MyEvents',
    component: () => import('@/pages/my/Events.vue'),
    meta: { permission: 'portal.access' },
  },
  {
    path: '/my/events/:name',
    name: 'MyEventDetail',
    component: () => import('@/pages/my/EventDetail.vue'),
    // Public calendar detail; same portal guard as the list.
    meta: { permission: 'portal.access' },
  },
  {
    path: '/my/certificates',
    name: 'MyCertificates',
    component: () => import('@/pages/my/Certificates.vue'),
    meta: { permission: 'portal.access' },
  },
  {
    path: '/my/formation',
    name: 'MyFormation',
    component: () => import('@/pages/my/Formation.vue'),
    meta: { permission: 'portal.access' },
  },
  {
    path: '/my/prayer',
    name: 'MyPrayer',
    component: () => import('@/pages/my/Prayer.vue'),
    meta: { permission: 'portal.access' },
  },
  {
    path: '/my/statement',
    name: 'MyStatement',
    component: () => import('@/pages/my/Statement.vue'),
    meta: { permission: 'portal.access' },
  },
  { path: '/:pathMatch(.*)*', name: 'NotFound', component: () => import('@/pages/NotFound.vue'), meta: { public: true },
  },
]

function normalizePath(path) {
  if (!path) return '/'
  if (path.startsWith('/church')) return path.slice('/church'.length) || '/'
  return path
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes })

let bootPromise = null
export function ensureBoot() {
  if (session.loaded) return Promise.resolve(session.boot)
  if (!bootPromise) {
    bootPromise = fetchBoot()
      .catch(() => session.boot)
      .finally(() => {
        bootPromise = null
      })
  }
  return bootPromise
}

router.beforeEach(async (to) => {
  await ensureBoot()

  if (to.meta.public) {
    if (
      (to.name === 'Login' || to.name === 'ForgotPassword' || to.name === 'Signup') &&
      session.boot?.authenticated
    ) {
      const dest = normalizePath(session.boot.home_route || '/')
      if (dest === '/login' || dest === to.path) {
        return session.boot.is_staff ? '/' : session.boot.is_member ? '/my' : '/'
      }
      return dest
    }
    return true
  }

  if (!session.boot?.authenticated) {
    return { name: 'Login', query: { redirect: to.fullPath } }
  }

  // Safe Environment Officers and Catechists have their own area; the staff dashboard
  // (finance totals) is not theirs.
  const home = normalizePath(session.boot.home_route || '/')
  if (to.name === 'Dashboard' && home !== '/' && home !== '/login') return home

  if (!canAccessRoute(to.meta)) {
    if (session.boot.is_member && !session.boot.is_staff) {
      return to.name === 'MyHub' ? true : { name: 'MyHub' }
    }
    if (home !== '/' && home !== '/login') return to.path === home ? true : home
    return to.name === 'Dashboard' ? true : { name: 'Dashboard' }
  }
  return true
})

export default router
